import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, money } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const RANGES = [[30, "1M"], [90, "3M"], [365, "1Y"]];

const Delta = ({ v, label, testid }) => {
  const up = v > 0, flat = !v;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${flat ? "text-muted-foreground" : up ? "text-good" : "text-bad"}`} data-testid={testid}>
      <Icon className="h-3.5 w-3.5" /> {flat ? "" : up ? "+" : "−"}{money(Math.abs(v || 0))} <span className="text-muted-foreground font-normal">{label}</span>
    </span>
  );
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = new Date(label + "T00:00:00");
  return (
    <div className="rounded-xl border border-border bg-background px-3 py-2 shadow-lift text-xs">
      <p className="text-muted-foreground">{d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>
      <p className="font-mono-num font-semibold text-sm mt-0.5">{money(payload[0].value)}</p>
      <p className="text-muted-foreground">{payload[0].payload.items} pieces</p>
    </div>
  );
};

/**
 * Threadworth — what your closet is worth, tracked over time.
 * Gold is reserved for this number across the app.
 */
export const WorthChart = ({ compact = false, className = "" }) => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.worthHistory(days).then(setData).catch(() => setData({ points: [], worth: 0 })); }, [days]);

  return (
    <section className={`rounded-2xl border border-border bg-card p-5 md:p-6 ${className}`} data-testid="threadworth-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="eyebrow"><span className="h-1.5 w-1.5 rounded-full bg-gold" /> Threadworth</p>
          {data ? (
            <p className="font-mono-num font-semibold text-[34px] md:text-[40px] leading-none mt-2 tracking-tight" data-testid="threadworth-value">{money(data.worth)}</p>
          ) : <Skeleton className="h-10 w-40 mt-2" />}
          {data && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <Delta v={data.change_7d} label="this week" testid="threadworth-delta-7d" />
              <Delta v={data.change_30d} label="this month" testid="threadworth-delta-30d" />
            </div>
          )}
        </div>
        <div className="inline-flex rounded-full bg-secondary p-0.5" role="tablist" aria-label="Range">
          {RANGES.map(([d, l]) => (
            <button key={d} role="tab" aria-selected={days === d} onClick={() => setDays(d)} className={`px-2.5 h-7 rounded-full text-[11px] font-semibold transition-colors ${days === d ? "bg-background shadow-xs text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`threadworth-range-${l}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className={`${compact ? "h-28" : "h-40 md:h-48"} mt-4 -mx-2`} data-testid="threadworth-chart">
        {data ? data.points.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.points} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="worthFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--gold))" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="hsl(var(--gold))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" hide />
              <YAxis hide domain={["dataMin - 20", "dataMax + 20"]} />
              <Tooltip content={<Tip />} cursor={{ stroke: "hsl(var(--gray-200))", strokeWidth: 1 }} />
              <Area type="monotone" dataKey="worth" stroke="hsl(var(--gold-deep))" strokeWidth={2} fill="url(#worthFill)" dot={false} activeDot={{ r: 4, fill: "hsl(var(--gold-deep))", stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full rounded-xl border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground px-4 text-center">Your value history starts the day you add your first piece.</div>
        ) : <Skeleton className="h-full w-full rounded-xl" />}
      </div>

      {data && !compact && (
        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-border pt-4 text-sm">
          <div><p className="text-[11px] text-muted-foreground">Pieces</p><p className="font-mono-num font-semibold mt-0.5" data-testid="threadworth-pieces">{data.items}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Value when new</p><p className="font-mono-num font-semibold mt-0.5">{money(data.retail)}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Since day one</p><p className={`font-mono-num font-semibold mt-0.5 ${data.since_start >= 0 ? "text-good" : "text-bad"}`}>{data.since_start >= 0 ? "+" : "−"}{money(Math.abs(data.since_start))}</p></div>
        </div>
      )}
      {!compact && <Link to="/inventory" className="mt-4 inline-flex text-sm font-medium underline-offset-4 hover:underline" data-testid="threadworth-open-closet">See every piece →</Link>}
    </section>
  );
};
